import {
  AfterViewInit,
  computed,
  Directive,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
} from '@angular/core';

/**
 * The following directive is used to set the top offset.
 * By querying the dom with a class selector.
 * */

/*
 * @NOTE RESIZE OBSERVER interface reports changes to the dimensions of an Element's content or border box,
 *  or the bounding box of an SVGElement.
 * */
@Directive({
  selector: '[wcgStickyContentOffsetClassBased]',
})
export class StickyContentOffsetClassBasedDirective implements AfterViewInit {
  private readonly _el = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);

  ngAfterViewInit(): void {
    // Wait for DOM to settle
    setTimeout(() => this._setOffset(), 0);

    // Observe changes in the header height
    const header = document.querySelector('.page-header-sticky');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        // Creating an instance for ResizeObserver
        this._setOffset();
      });
      resizeObserver.observe(header); // Register the element ref to resize observer
    }
  }

  private _setOffset(): void {
    const header = document.querySelector('.page-header-sticky');
    if (header) {
      const headerHeight = header.getBoundingClientRect().height;
      const toolbarHeight = getComputedStyle(
        document.documentElement,
      ).getPropertyValue('--mat-toolbar-standard-height');
      const totalOffset = headerHeight + (parseInt(toolbarHeight, 10) || 0);
      this._renderer.setStyle(
        this._el.nativeElement,
        'top',
        `${totalOffset}px`,
      );
    }
  }
}

/**
 * The following directive is used to set the top offset.
 * By using ElementRef to get the height of the native element.
 * */

@Directive({
  selector: '[stickyContentOffsetElementBased]',
})
export class StickyContentOffsetElementBasedDirective {
  private readonly _elementRef = inject(ElementRef);
  heightChange = output<number>();

  constructor() {
    this._customElementRef();
  }

  private _customElementRef(): void {
    const el = this._elementRef?.nativeElement;
    if (!el) {
      return;
    }

    const _resizeObserver = new ResizeObserver(() => this._emitHeight(el)); // Register the element ref to resize observer
    _resizeObserver.observe(el); // Register the element ref to resize observer
    this._emitHeight(el);
  }

  private _emitHeight(el: HTMLElement) {
    const height = el.getBoundingClientRect().height;
    if (height) {
      this.heightChange.emit(height);
    }
  }
}

/**
 * The following directive is used to calculate and set the top offset
 * of the element
 * */
@Directive({
  host: {
    '[style.top]': 'topOffSet()', // Bind the computed topOffSet to the style.top of the host element
  },
  selector: '[stickyTopOffset]',
})
export class StickyTopOffsetDirective {
  readonly contentHeight = input<number | undefined>();
  readonly topOffSet = computed(() => `${this.contentHeight() ?? 0}px`);
}
